import {useEffect, useMemo, useState, useCallback, useRef} from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Divider, Typography,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Tooltip,
    CircularProgress
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import BankLoanDetails from '@/pages/components/audit/adjustment/adjust/dialogs/loan/sub/BankLoanDetail';
import {FsCategoryLine, useAdjustmentStore, useAdjustDraftStore, useFsAdjustStore, useCompanyAnnualStore, useFsCategoryStore} from '@/store/audit';
import {useAdjustDialogData} from '@/adjustment/hooks/useAdjustDialogData';
import {useWpGlInvoiceDetail} from '@/adjustment/hooks/useWpGlInvoiceDetail';
import WpGlInvoiceDetailDialog, {WpGlInvoiceDetailButton} from '@/pages/components/audit/adjustment/adjust/dialogs/basic/WpGlInvoiceDetailDialog';
import {WpHeader, WpPerCheck, WpInterestPerCheck, WpCheckToolbar} from '@/adjustment/adjust/dialogs/basic/common';
import {useWpIssueMark} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/useWpIssueMark';
import {buildIssueMarkPayload} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/wpStatusSaveHelper';
import {invalidateWpSpecialSyncQueries} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/invalidateWpSpecialSyncQueries';
import {useQueryClient} from '@tanstack/react-query';
import {saveOrUpdate, batchSaveOrUpdate} from '@/services/auditService/auditWorkingPaperService';
import {runWpSaveWithGlobalTx} from '@/services/auditService/wpSaveTxService';
import {requiresWpGlobalTx} from '@/services/auditService/wpCrossServiceCodes';
import {notifyFsAdjustmentSilentRefresh} from '@/utils/fsAdjustmentRefresh';
import {
    getWpYoaMapCache,
    projectYoaBucketToWorkingPaperData,
    upsertWpYoaBucketDetails,
} from '@/services/auditService/wpYoaMapCache';
import {resolvePlClientAmountsForYoa} from '@/pages/components/audit/adjustment/hooks/plYoaClientAmounts';
import {
    buildBankLoanMultiYearBatchPayload,
    buildBankLoanMultiYearDeletePayload,
    applyBankLoanForwardRollFromPriors,
    refreshBankLoanSumsForWindow,
    bankLoanAllPriorOriginsFingerprint,
    bankLoansDisplayFingerprint,
    sanitizeBankLoansDetailsForSave,
    removeMatchingBankLoans,
    sumPeriodWindow,
    extractBankLoans,
    findPriorBankLoanForLastYoaDisplay,
    resolveBankLoanLastYoaColumnFromPrior,
    bankLoanPriorYearEndBalance,
    type BankLoanYoaPeriodSlice,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/bankLoanMultiYearSave';
import {
    sanitizeBankLoanSumsForSave,
    mergeBankLoanSumsPreserveChanges,
    mergeBankLoanSumsFromScheduleRecalc,
    isBankLoanScheduleUserRecalc,
    toNegativeBankLoanRepayment,
    resolveBankLoanBfFromSummaryLastYoaTotal,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/bankLoanSumCalc';
import {priorYearOfAssessment, resolvePriorPeriodSlice} from '@/utils/wpPriorPeriodBfRollForward';
import {formatToday, toNumber, formatNumber, convertToISOFormat} from '@/utils/dataUtils';
import {MoneyField} from '@/components/common/MoneyField';
import {PopoverButton} from '@/components/common';
import {OcrUploadProcessing} from '@/components/common/OcrUploadProcessing';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {SnackbarField} from '@/components/common/SnackbarField';
import {useWpDialogSnackbar} from '@/adjustment/hooks/WpSnackbarContext';
import type {ShowSnackbarFn} from '@/adjustment/adjust/dialogs/utils/working-helpers';
import {useWpIdleAutoSave} from '@/hooks/useWpIdleAutoSave';
import {dialogContentElRef} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/dialogContentElRef';
import {AutoSaveStatus} from '@/pages/components/audit/adjustment/adjust/dialogs/basic/common/AutoSaveStatus';
import {
    resolveWpPersistOptions,
    type WpPersistOptions,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/wpPersistOptions';
import {
    calculateIsUnbalanced,
    extractRowsDetails,
} from '@/adjustment/adjust/support';
import {BankLoanRow} from "@/services/auditService/interface/wp-row";
import {BankLoanWorkingPaper} from '@/services/auditService/interface/loan';
import {AdjustmentMode} from '@/services/auditService/adjustmentModes';
import userStore from '@/store/admin/userStore';
import EpWorkingContent from '@/adjustment/adjust/support/EpWorkingContent';
import {useSeamlessWpChrome} from '@/adjustment/hooks/useSeamlessWpChrome';
import {WpSeamlessPeriodTabsSlot} from '@/adjustment/hooks/WpPeriodTabsUi';
import {WpNotesAnchor} from '@/adjustment/adjust/support/WpNotesContext';
import {getCompany} from '@/services/accountService/companyService';
import RepaymentScheduleDrawer, {type RepaymentScheduleDrawerDataLoaded, type BankLoanSums} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/RepaymentScheduleDrawer';
import type {LoanScheduleRequest} from '@/services/auditService/auditLoanCalculatorService';

import '@mui/icons-material/Attachment';
import RichTextEditorDrawer from '@/adjustment/adjust/support/RichTextEditorDrawer';
import {AttachmentIconButton} from '@/adjustment/adjust/support';
import LoanFieldAdjustButton from '@/pages/components/audit/adjustment/adjust/dialogs/loan/LoanFieldAdjustButton';
import LoanFieldAdjustPanel, {
    type LoanFieldAdjustTarget,
    type LoanFieldAdjustActionState,
} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/LoanFieldAdjustPanel';
import LoanWpSkeleton from '@/pages/components/audit/adjustment/adjust/dialogs/loan/LoanWpSkeleton';
import {getLoanRowFieldAdj, sumLoanFieldAdjList} from '@/pages/components/audit/adjustment/adjust/dialogs/loan/loanFieldAdjUtils';
import LoanAdjAmountBadge from '@/pages/components/audit/adjustment/adjust/dialogs/loan/LoanAdjAmountBadge';
import {AdjustmentDetailsDrawer} from '@/adjustment/adjust/dialogs';
import {useAdjustmentDetailsDrawer} from '@/adjustment/hooks/useAdjustmentDetailsDrawer';

export type BankLoneProps = {
    open: boolean;
    onClose: () => void;
    payload?: {
        title?: string;
        annualId?: number;
        registryKey?: string;
        statement?: 'BS' | 'PL';
        companyId?: any;
        line?: FsCategoryLine;
        parDetail?: any;
        setParDetail?: (data: any) => void;
        workingRecordData?: any[] | null;
        workingRecordLoading?: boolean;
    } & Record<string, any>;
    embed?: boolean;
    onSnackbar?: ShowSnackbarFn;
};

const presetRowNames: string[] = [
    'Bank mortgage loan (secured) Interest paid',
    'Obligation under finance lease Hire purchase interest paid',
    '', // 两行利息的总和（无行名，只在total列计算）
    '', // 间隔行
    'Current portion',
    'Non-current portion',
    '', // Current + Non-Current 行名为空
    '', // 空行间隔
    'b/f (Last year audit report)',
    'Newly obtained bank loan',
    'Repayment during the year',
    'c/f',
];

// Types moved from LoanTable
interface LoanTableRow {
    id: string | number;
    name: string;
    editable: boolean;
    values: Record<string, number | string | null | undefined>;
}

interface ColumnDef {
    id: string;
    name: string;
    type: 'bank';
}

const genId = () => Math.random().toString(36).slice(2, 10);

/**
 * Newly obtained bank loan for a YOA Tab:
 * Loan amt when firstRepay >= that Tab's periodFrom; else 0.
 */
function calcBankLoanNewlyObtained(
    loan: Pick<Partial<BankLoanRow>, 'firstRepay' | 'loanAmt'> | null | undefined,
    periodFromStr?: string | null,
): number {
    if (!loan?.firstRepay || !periodFromStr) return 0;
    try {
        const [day, month, year] = String(loan.firstRepay).split('/');
        const firstRepayDate = new Date(`${year}-${month}-${day}`);
        const periodFromDate = new Date(periodFromStr);
        if (Number.isNaN(firstRepayDate.getTime()) || Number.isNaN(periodFromDate.getTime())) return 0;
        if (firstRepayDate >= periodFromDate) return Number(loan.loanAmt) || 0;
    } catch {
        /* ignore parse errors */
    }
    return 0;
}

function bankLoanPortionFromSums(
    sums: BankLoanRow['sums'] | undefined,
    kind: 'current' | 'nonCurrent',
): number {
    if (!sums) return 0;
    if (kind === 'current') {
        return toNumber(sums.currentYoaChange != null ? sums.currentYoaChange : sums.currentYoa);
    }
    return toNumber(
        sums.nonCurrentYoaChange != null
            ? sums.nonCurrentYoaChange
            : sums.nonCurrentYoa,
    );
}

function sumBankLoanPortions(loans: BankLoanRow[]): {currentPortion: number; nonCurrentPortion: number} {
    return loans.reduce(
        (acc, loan) => ({
            currentPortion: acc.currentPortion + bankLoanPortionFromSums(loan?.sums, 'current'),
            nonCurrentPortion: acc.nonCurrentPortion + bankLoanPortionFromSums(loan?.sums, 'nonCurrent'),
        }),
        {currentPortion: 0, nonCurrentPortion: 0},
    );
}

export default function BankLoan({open, onClose, payload, embed, onSnackbar}: BankLoneProps) {
    const companyName = useFsCategoryStore((s) => s.companyName);
    const headerAnnualId = useAdjustmentStore((s) => Number(s.header?.annualId || 0) || 0);
    const derivedAnnualId = useMemo(
        () => Number(payload?.annualId || 0) || headerAnnualId || 0,
        [payload?.annualId, headerAnnualId]
    );
    
    // Get firstAudit from companyAnnualStore
    const firstAudit = useCompanyAnnualStore((s) => s.byAnnualId[derivedAnnualId]?.firstAudit);
    // First MULTI Tab has no prior slice — schedule lastYear falls back to engagement annual windows
    const annualLastYearPeriodFrom = useCompanyAnnualStore(
        (s) => s.byAnnualId[derivedAnnualId]?.lastYearPeriodFrom ?? null,
    );
    const annualLastYearPeriodTo = useCompanyAnnualStore(
        (s) => s.byAnnualId[derivedAnnualId]?.lastYearPeriodTo ?? null,
    );

    const scheduleName = useMemo(() => {
        return payload?.line?.scheduleName || '';
    }, [payload?.line?.scheduleName]);

    const isControlled = useMemo(() => payload?.setParDetail !== undefined, [payload]);
    const isBreakdownMode = useMemo(() => payload?.mode === AdjustmentMode.BREAKDOWM, [payload?.mode]);

    // 无论通过 BS-NCL-BLS、PL-FC-LI 还是 BS-CL-BLS 访问，后端请求统一使用 BS-CL-BLS
    const backendCode = useMemo(() => {
        const raw = payload?.code || payload?.registryKey || payload?.line?.code || '';
        if (raw === 'BS-NCL-BLS' || raw === 'PL-FC-LI') return 'BS-CL-BLS';
        return raw;
    }, [payload?.code, payload?.registryKey, payload?.line?.code]);

    // 编辑模式判断：移除模式限制，始终允许编辑
    const isEditableMode = true;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Validation errors state
    type LoanValidationErrors = {
        [colId: string]: {
            [field: string]: string;
        };
    };
    const [validationErrors, setValidationErrors] = useState<LoanValidationErrors>({});

    const {
        meta,
        currentData,
        updateData,
        initialized,
    } = useAdjustDialogData({
        open,
        annualId: derivedAnnualId,
        code: backendCode,
        companyId: Number(payload?.companyId),
        isControlled,
        parDetail: payload?.parDetail,
        setParDetail: payload?.setParDetail,
        line: payload?.line,
        initialAdjustment: payload?.initialAdjustment,
        isToPaper: false,
        preferBackendData: !isControlled,
        workflow: payload?.workflow,
        initialDataFactory: useCallback((line: FsCategoryLine, initialAdjustment?: number) => ({
            bankLoans: []
        }), []),
    });

    // 附件弹窗状态
    const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
    const [currentAttachmentLoanId, setCurrentAttachmentLoanId] = useState<number | null>(null);
    const [currentAttachmentName, setCurrentAttachmentName] = useState<string>('');
    const userId = userStore.getCurrentUserId();

    const handleOpenAttachment = (loanColId: string) => {
        const loanData = loanDataByColumn[loanColId];
        if (loanData && loanData.id) {
            setCurrentAttachmentLoanId(loanData.id);
            setCurrentAttachmentName(loanColId);
            setAttachmentDialogOpen(true);
        } else {
            showSnackbar('Please save the loan first before adding attachments', 'warning');
        }
    };

    // Use ref for currentData to prevent unstable callbacks
    const currentDataRef = useRef(currentData);
    useEffect(() => {
        currentDataRef.current = currentData;
    }, [currentData]);

    const {yoa, lastYoa, currency, categoryLine, periodTo, periodFrom} = meta;

    const {
        isMultiPeriodTabs,
        headerPeriodFrom,
        headerPeriodTo,
        epWorkingMeta,
    } = useSeamlessWpChrome({
        open,
        payload,
        apiCode: backendCode,
        currentData,
        metaPeriodFrom: periodFrom,
        metaPeriodTo: periodTo,
    });

    /** MULTI：仅首个 YOA Tab 允许删贷款 / 编辑结转源。 */
    const isFirstYoaTab = useMemo(() => {
        if (!isMultiPeriodTabs) return true;
        const periods = Array.isArray(payload?.periods) ? payload.periods : [];
        const firstKey = String(periods[0]?.key || '').trim();
        const activeKey = String(payload?.periodScope?.sliceKey || '').trim();
        return !!firstKey && !!activeKey && firstKey === activeKey;
    }, [isMultiPeriodTabs, payload?.periods, payload?.periodScope?.sliceKey]);

    /** MULTI Tab → schedule / Newly obtained / Summary windows for the active Tab only. */
    const tabPeriodWindow = useMemo(() => {
        if (!isMultiPeriodTabs) return null;
        const periods = (Array.isArray(payload?.periods) ? payload.periods : []) as BankLoanYoaPeriodSlice[];
        const activeKey = String(payload?.periodScope?.sliceKey || '').trim();
        const index = periods.findIndex((p) => String(p?.key || '').trim() === activeKey);
        if (index >= 0) {
            const w = sumPeriodWindow(periods, index);
            if (index === 0) {
                return {
                    ...w,
                    lastYearPeriodFrom: w.lastYearPeriodFrom ?? annualLastYearPeriodFrom,
                    lastYearPeriodTo: w.lastYearPeriodTo ?? annualLastYearPeriodTo,
                };
            }
            return w;
        }
        const scope = payload?.periodScope;
        if (scope?.periodFrom || scope?.periodTo) {
            return {
                periodFrom: scope.periodFrom ?? null,
                periodTo: scope.periodTo ?? null,
                lastYearPeriodFrom: annualLastYearPeriodFrom,
                lastYearPeriodTo: annualLastYearPeriodTo,
            };
        }
        return null;
    }, [
        isMultiPeriodTabs,
        payload?.periods,
        payload?.periodScope,
        annualLastYearPeriodFrom,
        annualLastYearPeriodTo,
    ]);

    /** Active Tab periodFrom — MULTI must not use engagement meta.periodFrom for Newly obtained. */
    const activePeriodFrom = useMemo(
        () => (isMultiPeriodTabs ? (tabPeriodWindow?.periodFrom ?? periodFrom) : periodFrom) ?? null,
        [isMultiPeriodTabs, tabPeriodWindow?.periodFrom, periodFrom],
    );

    /** MULTI: Interest WpPerCheck YOA labels pinned to engagement span (not active Tab). */
    const stablePerCheckYoa = useMemo(() => {
        if (!isMultiPeriodTabs) {
            return {yoa: yoa ?? '', lastYoa: lastYoa ?? ''};
        }
        const periods = Array.isArray(payload?.periods) ? payload.periods : [];
        if (periods.length < 2) {
            return {yoa: yoa ?? '', lastYoa: lastYoa ?? ''};
        }
        const first = periods[0];
        const last = periods[periods.length - 1];
        const firstKey = String(first?.key || '').trim();
        const activeKey = String(payload?.periodScope?.sliceKey || '').trim();
        const onFirstTab = !!firstKey && !!activeKey && activeKey === firstKey;
        const firstYoaLabel = String(first?.yearOfAssessment || '').trim();
        // First-year first Tab: title is the first YOA (not engagement last YOA).
        const yoaLabel = (Number(firstAudit) === 1 && onFirstTab && firstYoaLabel)
            ? firstYoaLabel
            : String(last?.yearOfAssessment || yoa || '').trim();
        const activeTabYoa = String(
            payload?.periodScope?.yearOfAssessment
            || periods.find((p) => String(p?.key || '').trim() === activeKey)?.yearOfAssessment
            || firstYoaLabel
            || '',
        ).trim();
        const lastYoaLabel = priorYearOfAssessment(activeTabYoa) || lastYoa || '';
        return {yoa: yoaLabel, lastYoa: lastYoaLabel};
    }, [
        isMultiPeriodTabs,
        payload?.periods,
        payload?.periodScope?.sliceKey,
        payload?.periodScope?.yearOfAssessment,
        yoa,
        lastYoa,
        firstAudit,
    ]);

    /**
     * MULTI Period Tabs: SummaryCard last-YOA header advances with each Tab.
     * Tab0 = meta.lastYoa (API); Tab N = prior Tab YOA (or lastYoa shifted by N).
     * SINGLE unchanged (#20). Same pattern as PPE / CommonInvestmentWp.
     */
    const displayLastYoa = useMemo(() => {
        if (!isMultiPeriodTabs) return lastYoa;
        const periods = Array.isArray(payload?.periods) ? payload.periods : [];
        if (periods.length < 2) return lastYoa;
        const activeKey = String(payload?.periodScope?.sliceKey || '').trim();
        const idx = periods.findIndex((p) => String(p?.key || '').trim() === activeKey);
        if (idx <= 0) return lastYoa;
        const priorYoa = periods[idx - 1]?.yearOfAssessment;
        if (priorYoa) return priorYoa;
        const raw = String(lastYoa || '').trim();
        const m = /^(\d{4})\s*\/\s*(\d{2})$/.exec(raw);
        if (!m) return lastYoa;
        const left = Number(m[1]) + idx;
        const right = (Number(m[2]) + idx) % 100;
        return `${left}/${String(right).padStart(2, '0')}`;
    }, [
        isMultiPeriodTabs,
        payload?.periods,
        payload?.periodScope?.sliceKey,
        lastYoa,
    ]);


    const {issueMarkState, setIssueMarkState, validateIssueMarkBeforeSave} = useWpIssueMark({
        companyId: Number(payload?.companyId),
        annualId: derivedAnnualId,
        line: payload?.line,
        categoryLine,
    });

    const [interestPerOk, setInterestPerOk] = useState(true);
    const [currentPortionPerOk, setCurrentPortionPerOk] = useState(true);
    const [nonCurrentPortionPerOk, setNonCurrentPortionPerOk] = useState(true);
    const queryClient = useQueryClient();
    // GlCateDetail Dialog 状态（仅 workflow === 'account' 时使用）
    const glDetail = useWpGlInvoiceDetail(currentData);
    const glCode = payload?.code || payload?.registryKey || payload?.line?.code || '';

    const {showSnackbar, notifySaveResult, snackbarFieldProps} = useWpDialogSnackbar(onSnackbar, payload?.onSnackbar);

    // 2.1: Store incorporation date for validation
    const [inCorpDate, setInCorpDate] = useState<string | null>(null);

    // 存储从 VehicleLoan 获取的 HP Interest 总额
    const [vehicleHpInterest, setVehicleHpInterest] = useState<number>(0);

    // Flag to trigger data initialization after columns are set
    const [needsDataInitialization, setNeedsDataInitialization] = useState(false);

    // RepaymentScheduleDrawer states
    const [scheduleDrawerOpen, setScheduleDrawerOpen] = useState(false);
    const [currentScheduleRequest, setCurrentScheduleRequest] = useState<LoanScheduleRequest | null>(null);
    const [currentLoanColId, setCurrentLoanColId] = useState<string>('');

    // OCR upload processing overlay state
    const [ocrProcessingOpen, setOcrProcessingOpen] = useState(false);
    const [ocrProcessingComplete, setOcrProcessingComplete] = useState(false);

    const handleOcrProcessingStart = useCallback(() => {
        setOcrProcessingOpen(true);
        setOcrProcessingComplete(false);
    }, []);

    const handleOcrProcessingComplete = useCallback(() => {
        setOcrProcessingComplete(true);
    }, []);

    const handleOcrProcessingError = useCallback(() => {
        setOcrProcessingOpen(false);
        setOcrProcessingComplete(false);
    }, []);

    const handleOcrProcessingFinished = useCallback(() => {
        setOcrProcessingOpen(false);
        setOcrProcessingComplete(false);
    }, []);

    // 从 fsAdjustStore 获取 NON_BANK_LOAN 节点（通过 scheduleCode 索引，包含 fsId）
    const nonBankLoanStLine = useFsAdjustStore(state => state.getLineByCode('NON_BANK_LOAN'));

    // 获取 PL 树的原始数据（避免在 selector 中调用 getPlTree，因为它每次都创建新数组）
    const plLineRaw = useFsAdjustStore(state => state.adjustRecord?.plLine);

    // 在 useMemo 中处理 PL 树查找，避免 selector 返回新引用导致无限循环
    const hirePurchaseInterestLine = useMemo(() => {
        if (!plLineRaw || !Array.isArray(plLineRaw)) return null;

        // 在 PL 树中递归查找 Hire Purchase interest 的节点（优先用 code/scheduleCode，避免 fsId 在不同环境不一致）
        const TARGET_CODE = 'PL-FC-LI';
        const findNode = (node: any): any => {
            const nodeKey = String(node?.code || node?.scheduleCode || '').trim();
            if (nodeKey && nodeKey === TARGET_CODE) return node;
            if (Array.isArray(node.children)) {
                for (const child of node.children) {
                    const found = findNode(child);
                    if (found) return found;
                }
            }
            return null;
        };

        // plLineRaw 是数组，需要遍历每个根节点
        for (const rootNode of plLineRaw) {
            const found = findNode(rootNode);
            if (found) return found;
        }

        return null;
    }, [plLineRaw]);

    // 获取 BS/PL 树节点（field adjust Client = openingAmount）
    // 使用 byCode 索引，它返回稳定的引用
    const currentPortionLine = useFsAdjustStore(state => state.byCode['BS-CL-BLS'] ?? null); // fsId = 35
    const nonCurrentPortionLine = useFsAdjustStore(state => state.byCode['BS-NCL-BLS'] ?? null); // fsId = 44
    const loanInterestLine = useFsAdjustStore(state => state.byCode['PL-FC-LI'] ?? null);

    /** FS node Client for LoanFieldAdjustPanel (same total for every loan column). */
    const fieldAdjustPerClientByKey = useMemo(() => {
        const clientOf = (line: {openingAmount?: unknown; amount?: unknown} | null) =>
            Number(line?.openingAmount ?? line?.amount ?? 0) || 0;
        return {
            currentPortion: clientOf(currentPortionLine),
            nonCurrentPortion: clientOf(nonCurrentPortionLine),
            interest: clientOf(loanInterestLine),
        } as Record<string, number>;
    }, [currentPortionLine, nonCurrentPortionLine, loanInterestLine]);

    // 从 currentData 提取所有数据 - 直接读取 bankLoans，避免 extractRowsDetails 的 scheduleCode 匹配问题
    const bankLoans = useMemo(() => {
        // 优先从 currentData.bankLoans 读取
        if (currentData && typeof currentData === 'object') {
            if (Array.isArray(currentData.bankLoans)) {
                return currentData.bankLoans;
            }
            // 兼容数组格式 [{ bankLoans: [...] }]
            if (Array.isArray(currentData) && currentData.length > 0 && Array.isArray(currentData[0]?.bankLoans)) {
                return currentData[0].bankLoans;
            }
        }
        return [];
    }, [currentData]);

    const bankLoansFp = useMemo(() => bankLoansDisplayFingerprint(bankLoans), [bankLoans]);
    const activeSliceKey = String(payload?.periodScope?.sliceKey || '').trim();
    const prevActiveSliceKeyRef = useRef(activeSliceKey);

    // MULTI tab switch: wipe table cells from the previous Tab before draft/roll re-fills this Tab.
    useEffect(() => {
        if (!isMultiPeriodTabs) {
            prevActiveSliceKeyRef.current = activeSliceKey;
            return;
        }
        if (prevActiveSliceKeyRef.current === activeSliceKey) return;
        prevActiveSliceKeyRef.current = activeSliceKey;
        setRows(presetRowNames.map((name, idx) => ({
            id: idx + 1,
            name,
            editable: name === 'b/f (Last year audit report)',
            values: {},
        })));
        setNeedsDataInitialization(true);
    }, [activeSliceKey, isMultiPeriodTabs]);

    // MULTI：从上一 YOA Tab draft 结转 Bank / Loan ac no + Current / Non-current（后台算 sums）
    const bankLoanRollAppliedRef = useRef<string | null>(null);
    const bankLoanRollSliceReadyRef = useRef<string | null>(null);
    /** MULTI first Tab YOA-window sums refresh (once per signature). */
    const bankLoanFirstTabSumsRef = useRef<string | null>(null);
    useEffect(() => {
        if (!open || !initialized || !currentData) return;
        if (!isMultiPeriodTabs) return;
        const periods = Array.isArray(payload?.periods) ? payload.periods : [];
        if (!resolvePriorPeriodSlice(periods, activeSliceKey)) {
            bankLoanRollAppliedRef.current = null;
            bankLoanRollSliceReadyRef.current = activeSliceKey || null;
            return;
        }
        // sliceKey 先变、draft 后到 —— 等一轮再 roll，避免用上年数据写进新年草稿
        if (bankLoanRollSliceReadyRef.current !== activeSliceKey) {
            bankLoanRollSliceReadyRef.current = activeSliceKey || null;
            bankLoanRollAppliedRef.current = null;
            return;
        }

        const getDetail = (key: string) => useAdjustDraftStore.getState().getUnifiedDetail(key);
        const priorFp = bankLoanAllPriorOriginsFingerprint(periods, activeSliceKey, getDetail, backendCode);
        const signature = `${activeSliceKey}|${priorFp}|${bankLoansFp}`;
        if (bankLoanRollAppliedRef.current === signature) return;

        let cancelled = false;
        bankLoanRollAppliedRef.current = signature;
        (async () => {
            const next = await applyBankLoanForwardRollFromPriors(
                currentData,
                periods,
                activeSliceKey,
                getDetail,
                backendCode,
            );
            if (cancelled || !next) return;
            updateData(next);
        })();
        return () => {
            cancelled = true;
        };
    }, [
        open,
        initialized,
        currentData,
        updateData,
        backendCode,
        isMultiPeriodTabs,
        payload?.periods,
        activeSliceKey,
        bankLoansFp,
    ]);

    useEffect(() => {
        if (!open) {
            bankLoanRollAppliedRef.current = null;
            bankLoanRollSliceReadyRef.current = null;
            bankLoanFirstTabSumsRef.current = null;
        }
    }, [open]);

    const adjDrawer = useAdjustmentDetailsDrawer();
    const [fieldAdjustTarget, setFieldAdjustTarget] = useState<LoanFieldAdjustTarget | null>(null);
    const [fieldAdjustAction, setFieldAdjustAction] = useState<LoanFieldAdjustActionState | null>(null);

    const handleFieldAdjustActivate = useCallback((target: LoanFieldAdjustTarget) => {
        setFieldAdjustTarget(target);
    }, []);

    const handleFieldAdjustClose = useCallback(() => {
        setFieldAdjustTarget(null);
        setFieldAdjustAction(null);
    }, []);

    const handleFieldAdjustActionStateChange = useCallback((state: LoanFieldAdjustActionState | null) => {
        setFieldAdjustAction(state);
    }, []);

    const showFieldAdjustmentButton = !!fieldAdjustAction?.shouldShowAdjustmentButton;

    // 对话框关闭时重置字段调整面板
    useEffect(() => {
        if (!open) {
            setFieldAdjustTarget(null);
            setFieldAdjustAction(null);
        }
    }, [open]);

    // 2.1: Fetch incorporation date
    useEffect(() => {
        const fetchIncorpDate = async () => {
            if (!payload?.companyId) return;
            try {
                const company = await getCompany(Number(payload.companyId));
                setInCorpDate(company?.inCorpDate || null);
            } catch (error) {
                console.error('Failed to fetch company incorporation date:', error);
            }
        };
        fetchIncorpDate();
    }, [payload?.companyId]);

    // columns 和 rows 需要本地管理，因为它们不是 BankLoanWorkingPaper 的一部分
    const [columns, setColumns] = useState<ColumnDef[]>([]);

    const [rows, setRows] = useState<LoanTableRow[]>(() =>
        presetRowNames.map((name, idx) => ({
            id: idx + 1,
            name,
            editable: name === 'b/f (Last year audit report)',
            values: {},
        }))
    );

    // Add Bank Loan dialog (open details first; commit row only on confirm)
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [addDialogKey, setAddDialogKey] = useState(0);
    const pendingApplyColIdRef = useRef<string | null>(null);

    // 构建 loanDataByColumn map
    const loanDataByColumn = useMemo(() => {
        const columnMap: Record<string, Partial<BankLoanRow>> = {};
        let bankIdx = 0;
        columns.forEach((col) => {
            if (col.type === 'bank' && bankLoans[bankIdx]) {
                columnMap[col.id] = bankLoans[bankIdx];
                bankIdx++;
            }
        });
        return columnMap;
    }, [columns, bankLoans]);

    /**
     * MULTI later Tab: prior YOA bankLoans for Summary last-YOA display
     * (prior current-column Change ?? calc). Subscribe to draft so prior *Change edits refresh.
     */
    const priorTabDraftKey = useMemo(() => {
        if (!isMultiPeriodTabs || isFirstYoaTab) return '';
        const periods = Array.isArray(payload?.periods) ? payload.periods : [];
        const prior = resolvePriorPeriodSlice(periods, activeSliceKey);
        if (!prior?.priorKey) return '';
        return `${backendCode}__${prior.priorKey}`;
    }, [isMultiPeriodTabs, isFirstYoaTab, payload?.periods, activeSliceKey, backendCode]);

    const priorTabDetail = useAdjustDraftStore(
        useCallback((s) => (priorTabDraftKey ? s.getUnifiedDetail(priorTabDraftKey) : null), [priorTabDraftKey]),
    );

    const priorTabBankLoans = useMemo(
        () => extractBankLoans(priorTabDetail),
        [priorTabDetail],
    );

    const resolveLastYoaColumnDisplay = useCallback((loan: BankLoanRow | null | undefined) => {
        if (!isMultiPeriodTabs || isFirstYoaTab || !loan) return null;
        const prior = findPriorBankLoanForLastYoaDisplay(loan, priorTabBankLoans);
        return resolveBankLoanLastYoaColumnFromPrior(prior);
    }, [isMultiPeriodTabs, isFirstYoaTab, priorTabBankLoans]);

    /** b/f = Detail SummaryCard last-YOA Total (same resolve as lastYoaTotalDisplay). */
    const resolveBfAmount = useCallback((loan: Partial<BankLoanRow> | null | undefined) => {
        return resolveBankLoanBfFromSummaryLastYoaTotal(
            loan,
            resolveLastYoaColumnDisplay(loan as BankLoanRow | null | undefined),
        );
    }, [resolveLastYoaColumnDisplay]);

    /** Same shape as saveDraft details — used for WP→WP fromPaper details */
    const buildBankLoanSaveDetails = useCallback(() => {
        const allBankLoans = columns
            .filter((c) => c.type === 'bank')
            .map((c) => loanDataByColumn[c.id])
            .filter((loan) => loan && Object.keys(loan).length > 0) as BankLoanRow[];

        const bankLoansForSave = allBankLoans.map((loan) => {
            const sums = loan.sums || {};
            const finalSums = sanitizeBankLoanSumsForSave({
                ...sums,
                // Keep calculated xxx; manual override stays in *Change only (Detail left vs right).
                // interestNext still mirrors effective current interest for WP table fallback.
                interestNext: sums.interestYoaChange != null ? sums.interestYoaChange : sums.interestNext,
            });
            const {adjustments: _adj, ...loanRest} = loan as BankLoanRow & {adjustments?: unknown};
            return {
                ...loanRest,
                // Persist b/f wire = Summary last-YOA Total (same as table / Detail)
                currentTotalInput: resolveBfAmount(loan),
                sums: finalSums,
                annualId: derivedAnnualId,
                code: 'BS-CL-BLS',
            };
        });

        // Same save wire as persist (incl. repayment as negative)
        return sanitizeBankLoansDetailsForSave({bankLoans: bankLoansForSave});
    }, [columns, loanDataByColumn, derivedAnnualId, resolveBfAmount]);

    // Update loan data function
    const updateLoanData = useCallback((newBankLoans: BankLoanRow[]) => {
        const updatedData: BankLoanWorkingPaper = {
            ...currentDataRef.current,
            bankLoans: newBankLoans,
        };
        // 同步更新 ref，确保 saveDraft 可以立即读取到最新数据
        currentDataRef.current = updatedData;
        updateData(updatedData);
    }, [updateData]); // Removed currentData from deps

    // Apply calculator result to a specific column
    const applyCalculatorResultToColumn = useCallback((colId: string) => {
        // 从 currentData 重新提取最新的 bankLoans 数据
        // 统一使用 backendCode（BS-CL-BLS），避免 PL-FC-LI 等别名 code 导致 extractRowsDetails 走错分支
        const latestData = extractRowsDetails(currentDataRef.current, undefined, 'loan', backendCode) as BankLoanWorkingPaper;
        const latestBankLoans = latestData?.bankLoans || [];

        // 重新构建 columnMap 以获取最新数据
        const latestColumnMap: Record<string, Partial<BankLoanRow>> = {};
        let bankIdx = 0;
        columns.forEach((col) => {
            if (col.type === 'bank' && latestBankLoans[bankIdx]) {
                latestColumnMap[col.id] = latestBankLoans[bankIdx];
                bankIdx++;
            }
        });

        const loanData = latestColumnMap[colId];
        if (!loanData) return;

        // Type guard to check if it's a bank loan
        const isBankLoan = 'interestYoa' in (loanData.sums || {}) || 'interestCurr' in (loanData.sums || {});

        // 更新 rows 数据
        setRows(prevRows => {
            // 第一步：更新指定列的数据
            const updated = prevRows.map((r) => {
                const next = {...r, values: {...r.values}};

                if (isBankLoan) {
                    const bankData = loanData as Partial<BankLoanRow>;
                    if (r.name === 'Bank mortgage loan (secured) Interest paid') {
                        // Interest for current year - use xxxChange if available
                        const interestValue = bankData?.sums?.interestYoaChange != null 
                            ? bankData.sums.interestYoaChange 
                            : (bankData?.sums?.interestNext || 0);
                        next.values[colId] = formatNumber(interestValue);
                    } else if (r.name === 'Current portion') {
                        // 1.1.1: bd(summary - <1yr (current year)) = sums.currentYoa - use xxxChange if available
                        const principalNextValue = bankData?.sums?.currentYoaChange != null 
                            ? bankData.sums.currentYoaChange 
                            : (bankData?.sums?.currentYoa || 0);
                        next.values[colId] = formatNumber(principalNextValue);
                    } else if (r.name === 'Non-current portion') {
                        // 1.1.2: bd(>1yr (current year)) = sums.nonCurrentYoa - use xxxChange if available
                        const principalAfterNextToValue = bankData?.sums?.nonCurrentYoaChange != null 
                            ? bankData.sums.nonCurrentYoaChange 
                            : (bankData?.sums?.nonCurrentYoa || 0);
                        next.values[colId] = formatNumber(principalAfterNextToValue);
                    } else if (r.id === 7) {
                        // 1.1.3: Current + Non-Current (行名为空) - use xxxChange if available
                        const principalNextValue = bankData?.sums?.currentYoaChange != null 
                            ? bankData.sums.currentYoaChange 
                            : (bankData?.sums?.currentYoa || 0);
                        const principalAfterNextToValue = bankData?.sums?.nonCurrentYoaChange != null 
                            ? bankData.sums.nonCurrentYoaChange 
                            : (bankData?.sums?.nonCurrentYoa || 0);
                        const sum = principalNextValue + principalAfterNextToValue;
                        next.values[colId] = formatNumber(sum);
                    } else if (r.name === 'b/f (Last year audit report)') {
                        // 1.1.4: Detail SummaryCard last-YOA Total
                        next.values[colId] = formatNumber(resolveBfAmount(bankData));
                    } else if (r.name === 'Newly obtained bank loan') {
                        // 1.1.5: firstRepay >= active Tab periodFrom → Loan amt; else 0
                        next.values[colId] = formatNumber(
                            calcBankLoanNewlyObtained(bankData, activePeriodFrom),
                        );
                    } else if (r.name === 'Repayment during the year') {
                        // 1.1.6: Drawer green Principal → outflow (negative) for WP table / c/f
                        const principalCurrValue = bankData?.sums?.currentLastYoaChange != null 
                            ? bankData.sums.currentLastYoaChange 
                            : (bankData?.sums?.currentLastYoa || 0);
                        next.values[colId] = formatNumber(toNegativeBankLoanRepayment(principalCurrValue));
                    }
                }
                return next;
            });

            // 第二步：c/f = Detail Summary YOA Total（Current + Non-current），与结转 year-end 同源
            const cfIndex = updated.findIndex((r) => r.name === 'c/f');
            if (cfIndex >= 0) {
                const cf = {...updated[cfIndex], values: {...updated[cfIndex].values}} as LoanTableRow;
                columns.forEach((col) => {
                    if (col.type !== 'bank') return;
                    cf.values[col.id] = formatNumber(
                        bankLoanPriorYearEndBalance(latestColumnMap[col.id] as BankLoanRow | undefined),
                    );
                });
                updated[cfIndex] = cf;
            }

            return updated;
        });
    }, [columns, activePeriodFrom, backendCode, resolveBfAmount]);

    // ✅ 旧的 fetchAdjustMeta useEffect 已删除，由 useAdjustDialogData 处理

    // 记录是否已初始化 columns
    const [columnsInitialized, setColumnsInitialized] = useState(false);

    // 初始化 / 同步 columns：SINGLE 仅首次；MULTI 随 sliceKey + bankLoans 同步（结转后表头 Bank / Loan ac no）
    useEffect(() => {
        if (!initialized) return;

        if (isMultiPeriodTabs) {
            if (bankLoans.length > 0) {
                setColumns((prev) => {
                    const next = bankLoans.map((_: BankLoanRow, index: number) => (
                        prev[index]?.type === 'bank'
                            ? {...prev[index], name: `${index + 1}`}
                            : {id: genId(), name: `${index + 1}`, type: 'bank' as const}
                    ));
                    return next;
                });
                setColumnsInitialized(true);
                setNeedsDataInitialization(true);
            } else {
                setColumns([]);
                // Clear previous Tab cell values so b/f / Newly obtained cannot bleed into empty Tab
                setRows(presetRowNames.map((name, idx) => ({
                    id: idx + 1,
                    name,
                    editable: name === 'b/f (Last year audit report)',
                    values: {},
                })));
                setColumnsInitialized(true);
                setNeedsDataInitialization(false);
            }
            return;
        }

        if (!bankLoans || columnsInitialized) return;
        if (bankLoans.length > 0) {
            const newColumns = bankLoans.map((_: any, index: number) => ({
                id: genId(),
                name: `${index + 1}`,
                type: 'bank' as const
            }));
            setColumns(newColumns);
            setColumnsInitialized(true);
            setNeedsDataInitialization(true);
        } else {
            setColumnsInitialized(true);
        }
    }, [initialized, bankLoans, bankLoansFp, columnsInitialized, isMultiPeriodTabs, activeSliceKey]);

    // 对话框关闭时重置初始化状态
    useEffect(() => {
        if (!open) {
            setColumnsInitialized(false);
            setNeedsDataInitialization(false);
            setOcrProcessingOpen(false);
            setOcrProcessingComplete(false);
            // Reset columns and rows to prevent stale state on reopen
            setColumns([]);
            setRows(presetRowNames.map((name, idx) => ({
                id: idx + 1,
                name,
                editable: name === 'b/f (Last year audit report)',
                values: {},
            })));
            setAddDialogOpen(false);
        }
    }, [open]);

    // Apply data to rows after columns are initialized from backend
    useEffect(() => {
        if (needsDataInitialization && columns.length > 0 && bankLoans.length > 0) {
            // Apply data to each column
            columns.forEach((col) => {
                applyCalculatorResultToColumn(col.id);
            });

            // Reset flag
            setNeedsDataInitialization(false);
        }
    }, [needsDataInitialization, columns, bankLoans, applyCalculatorResultToColumn]);

    // Apply calculator result for a newly confirmed loan after its column is committed
    useEffect(() => {
        const pendingId = pendingApplyColIdRef.current;
        if (!pendingId) return;
        if (!columns.some((c) => c.id === pendingId)) return;
        pendingApplyColIdRef.current = null;
        applyCalculatorResultToColumn(pendingId);
    }, [columns, applyCalculatorResultToColumn]);

    const today = useMemo(() => formatToday(), []);

    // Derive c/f row whenever rows change
    // 对列进行排序和重新编号：Bank Loan 在左
    const sortAndRenumberColumns = (cols: ColumnDef[]) => {
        // 只处理 Bank Loan
        const bankLoans = cols.filter(c => c.type === 'bank');

        // 编号
        const numberedBankLoans = bankLoans.map((col, idx) => ({
            ...col,
            name: `${idx + 1}`
        }));

        return numberedBankLoans;
    };

    const recomputeCarryForward = useCallback((baseRows: LoanTableRow[]) => {
        const nextRows = [...baseRows];
        const cfIndex = nextRows.findIndex((r) => r.name === 'c/f');
        if (cfIndex >= 0) {
            const cf = {...nextRows[cfIndex], values: {...nextRows[cfIndex].values}} as LoanTableRow;
            columns.forEach((col) => {
                if (col.type !== 'bank') return;
                // c/f = Detail Summary YOA Total（Current + Non-current）
                cf.values[col.id] = formatNumber(
                    bankLoanPriorYearEndBalance(loanDataByColumn[col.id] as BankLoanRow | undefined),
                );
            });
            nextRows[cfIndex] = cf;
        }
        setRows(nextRows);
    }, [columns, loanDataByColumn]);

    const openAddBankLoanDialog = () => {
        setAddDialogKey((k) => k + 1);
        setAddDialogOpen(true);
    };

    const handleAddBankLoanConfirm = useCallback((data: Partial<BankLoanRow>) => {
        const id = genId();
        const existingLoans = columns
            .filter((c) => c.type === 'bank')
            .map((c) => loanDataByColumn[c.id])
            .filter((loan) => loan && Object.keys(loan).length > 0) as BankLoanRow[];

        const newLoan = {
            ...data,
            code: data.code || 'BS-CL-BLS',
        } as BankLoanRow;

        pendingApplyColIdRef.current = id;
        updateLoanData([...existingLoans, newLoan]);
        setColumns((prev) => sortAndRenumberColumns([...prev, {id, name: '1', type: 'bank' as const}]));
        setAddDialogOpen(false);
    }, [columns, loanDataByColumn, updateLoanData]);

    const handleAddBankLoanCancel = useCallback(() => {
        setAddDialogOpen(false);
    }, []);

    // MULTI first Tab: no prior-roll — refresh sums for this Tab YOA window so
    // "Repayment during the year" matches Drawer Principal (and clear stale currentLastYoaChange: 0).
    useEffect(() => {
        if (!open || !initialized || !currentData) return;
        if (!isMultiPeriodTabs || !isFirstYoaTab) {
            bankLoanFirstTabSumsRef.current = null;
            return;
        }
        if (!tabPeriodWindow?.periodFrom && !tabPeriodWindow?.periodTo) return;
        if (bankLoans.length === 0) return;

        if (bankLoanRollSliceReadyRef.current !== activeSliceKey) {
            bankLoanRollSliceReadyRef.current = activeSliceKey || null;
            bankLoanFirstTabSumsRef.current = null;
            return;
        }

        const signature = [
            activeSliceKey,
            bankLoansFp,
            tabPeriodWindow.periodFrom ?? '',
            tabPeriodWindow.periodTo ?? '',
            tabPeriodWindow.lastYearPeriodFrom ?? '',
            tabPeriodWindow.lastYearPeriodTo ?? '',
        ].join('|');
        if (bankLoanFirstTabSumsRef.current === signature) return;

        let cancelled = false;
        bankLoanFirstTabSumsRef.current = signature;
        (async () => {
            const {loans, changed} = await refreshBankLoanSumsForWindow({
                loans: bankLoans as BankLoanRow[],
                window: tabPeriodWindow,
                clearStaleZeroPrincipalCurrChange: true,
            });
            if (cancelled || !changed) return;
            const base = (currentData && typeof currentData === 'object' && !Array.isArray(currentData))
                ? currentData
                : {};
            updateData({...base, bankLoans: loans});
        })();
        return () => {
            cancelled = true;
        };
    }, [
        open,
        initialized,
        currentData,
        updateData,
        isMultiPeriodTabs,
        isFirstYoaTab,
        activeSliceKey,
        bankLoans,
        bankLoansFp,
        tabPeriodWindow,
    ]);

    const canShowBankLoanDelete = isEditableMode && (!isMultiPeriodTabs || isFirstYoaTab);

    const [deletingLoanColId, setDeletingLoanColId] = useState<string | null>(null);

    const applyLocalColumnRemoval = useCallback((colId: string, remainingLoans: BankLoanRow[]) => {
        setColumns((prev) => sortAndRenumberColumns(prev.filter((c: ColumnDef) => c.id !== colId)));
        updateLoanData(remainingLoans);
        setRows((prev) => prev.map((r) => {
            const next = {...r, values: {...r.values}};
            delete next.values[colId];
            return next;
        }));
    }, [updateLoanData]);

    const removeColumn = useCallback(async (colId: string) => {
        const targetLoan = loanDataByColumn[colId] as BankLoanRow | undefined;
        const remainingColumns = columns.filter((c: ColumnDef) => c.id !== colId);
        const remainingLoans = remainingColumns
            .filter((c: ColumnDef) => c.type === 'bank')
            .map((c: ColumnDef) => loanDataByColumn[c.id])
            .filter((loan: any) => loan && Object.keys(loan).length > 0) as BankLoanRow[];

        // SINGLE / 非多年：只改本地，等 Save
        const multiPeriods = Array.isArray(payload?.periods) ? payload.periods : [];
        const isMultiDelete = isMultiPeriodTabs && multiPeriods.length >= 2 && !!targetLoan;
        if (!isMultiDelete) {
            applyLocalColumnRemoval(colId, remainingLoans);
            return;
        }

        setDeletingLoanColId(colId);
        try {
            const saveYoa = String(
                payload?.periodScope?.yearOfAssessment
                || (payload as {yearOfAssessment?: string})?.yearOfAssessment
                || yoa
                || '',
            ).trim();
            const activeDetails = sanitizeBankLoansDetailsForSave({
                bankLoans: remainingLoans.map((loan) => {
                    const sums = loan.sums || {};
                    return {
                        ...loan,
                        sums: sanitizeBankLoanSumsForSave(sums),
                        annualId: derivedAnnualId,
                        code: 'BS-CL-BLS',
                    };
                }),
            });
            const issueMarkPayload = buildIssueMarkPayload(
                issueMarkState,
                payload?.line?.wpStatus,
                Boolean((payload as any)?.line?.outstanding),
            );
            const byYearPayload = buildBankLoanMultiYearDeletePayload({
                periods: multiPeriods,
                apiCode: backendCode,
                companyId: Number(payload?.companyId) || 0,
                annualId: derivedAnnualId,
                targetLoan,
                activeYoa: saveYoa,
                activeSliceKey: payload?.periodScope?.sliceKey || '',
                activeDetails,
                getDetail: (key: string) => useAdjustDraftStore.getState().getUnifiedDetail(key),
                issueMark: issueMarkPayload ?? null,
            });

            if (!byYearPayload || !saveYoa) {
                applyLocalColumnRemoval(colId, remainingLoans);
                showSnackbar('Failed to remove bank loan across years', 'error');
                return;
            }

            const runBatch = () => batchSaveOrUpdate({
                companyId: Number(payload?.companyId),
                annualId: derivedAnnualId,
                workingPapers: {[backendCode]: byYearPayload},
            });
            if (requiresWpGlobalTx(backendCode)) {
                await runWpSaveWithGlobalTx(runBatch);
            } else {
                await runBatch();
            }

            // 同步各年 draft + YOA Map 缓存，避免 Tab 切回又结转回来
            const draftStore = useAdjustDraftStore.getState();
            for (const period of multiPeriods) {
                const yoaKey = String(period?.yearOfAssessment || '').trim();
                const sliceKey = String(period?.key || '').trim();
                const bucket = byYearPayload[yoaKey] as {details?: Record<string, unknown>} | undefined;
                const details = bucket?.details;
                if (!yoaKey || !details || typeof details !== 'object') continue;

                if (sliceKey) {
                    const draftKey = `${backendCode}__${sliceKey}`;
                    const prev = draftStore.getUnifiedDetail(draftKey);
                    const base = (prev && typeof prev === 'object' && !Array.isArray(prev)) ? prev : {};
                    draftStore.setUnifiedDetail(draftKey, {
                        ...base,
                        ...details,
                        bankLoans: Array.isArray((details as {bankLoans?: BankLoanRow[]}).bankLoans)
                            ? (details as {bankLoans: BankLoanRow[]}).bankLoans
                            : removeMatchingBankLoans(
                                Array.isArray((prev as {bankLoans?: BankLoanRow[]})?.bankLoans)
                                    ? (prev as {bankLoans: BankLoanRow[]}).bankLoans
                                    : [],
                                targetLoan,
                            ),
                    });
                }
                if (Number(payload?.companyId) > 0 && derivedAnnualId > 0) {
                    upsertWpYoaBucketDetails(
                        Number(payload?.companyId),
                        derivedAnnualId,
                        backendCode,
                        yoaKey,
                        details,
                    );
                }
            }

            applyLocalColumnRemoval(colId, remainingLoans);
            notifyFsAdjustmentSilentRefresh(Number(payload?.companyId), derivedAnnualId, backendCode);
            await invalidateWpSpecialSyncQueries(
                queryClient,
                Number(payload?.companyId),
                derivedAnnualId,
                backendCode,
                'BankLoan',
            );
            showSnackbar('Bank loan removed from all years', 'success');
        } catch (error: any) {
            showSnackbar(error?.message || 'Failed to remove bank loan', 'error');
        } finally {
            setDeletingLoanColId(null);
        }
    }, [
        loanDataByColumn,
        columns,
        payload,
        isMultiPeriodTabs,
        applyLocalColumnRemoval,
        yoa,
        derivedAnnualId,
        issueMarkState,
        backendCode,
        queryClient,
        showSnackbar,
    ]);

    const rowTotal = (row: LoanTableRow) => {
        // 对于 "Hire purchase interest paid" 行，显示从 VehicleLoan 获取的 HP Interest
        if (row.name === 'Obligation under finance lease Hire purchase interest paid') {
            return vehicleHpInterest;
        }
        // 对于 id=3 的行（两行利息的总和），只在 total 列显示，计算前两行的总和
        if (row.id === 3) {
            const bankInterestRow = rows.find(r => r.name === 'Bank mortgage loan (secured) Interest paid');
            const hpInterestRow = rows.find(r => r.name === 'Obligation under finance lease Hire purchase interest paid');
            const bankInterestTotal = bankInterestRow ? columns.reduce((acc, c) => acc + toNumber(bankInterestRow.values[c.id]), 0) : 0;
            const hpInterestTotal = vehicleHpInterest; // HP Interest 直接使用 vehicleHpInterest
            return bankInterestTotal + hpInterestTotal;
        }
        // 对于 id=7 的行（Current + Non-current 之和），只在 total 列显示
        if (row.id === 7) {
            const currentPortionRow = rows.find(r => r.name === 'Current portion');
            const nonCurrentPortionRow = rows.find(r => r.name === 'Non-current portion');
            const currentTotal = currentPortionRow ? columns.reduce((acc, c) => acc + toNumber(currentPortionRow.values[c.id]), 0) : 0;
            const nonCurrentTotal = nonCurrentPortionRow ? columns.reduce((acc, c) => acc + toNumber(nonCurrentPortionRow.values[c.id]), 0) : 0;
            return currentTotal + nonCurrentTotal;
        }
        // 其他行正常计算各列之和
        return columns.reduce((acc, c) => acc + toNumber(row.values[c.id]), 0);
    };

    const canDeleteColumn = (colId: string, tableRows: LoanTableRow[]) => {
        const repay = tableRows.find((r) => r.name === 'Repayment during the year');
        const v = repay?.values?.[colId];
        if (v === null || v === undefined) return true;
        if (typeof v === 'string') return v.trim() === '';
        return false;
    };

    // BankLoan required fields for validation
    const bankLoanRequiredFields = useMemo(() => ['bank', 'loanNo', 'loanAmt', 'ratePct', 'terms', 'firstRepay', 'currentTotalInput'], []);

    // Validate bank loan data - returns { valid, errors, filteredLoans }
    const validateBankLoans = useCallback((loansToValidate: BankLoanRow[]): {
        valid: boolean;
        errors: LoanValidationErrors;
        filteredLoans: BankLoanRow[]
    } => {
        const errors: LoanValidationErrors = {};
        const filteredLoans: BankLoanRow[] = [];
        let hasErrors = false;

        loansToValidate.forEach((loan, index) => {
            const colId = columns[index]?.id || String(index);
            const filledFields: string[] = [];
            const emptyFields: string[] = [];

            // Check each required field
            bankLoanRequiredFields.forEach(field => {
                const value = (loan as any)[field];
                if (value !== undefined && value !== null && value !== '' && value !== 0) {
                    filledFields.push(field);
                } else {
                    emptyFields.push(field);
                }
            });

            // If all fields are empty, skip this loan (will be filtered out)
            if (filledFields.length === 0) {
                return;
            }

            // If some fields have value but others don't, it's an error
            if (emptyFields.length > 0 && filledFields.length > 0) {
                errors[colId] = {};
                emptyFields.forEach(field => {
                    errors[colId][field] = `${field} is required`;
                });
                hasErrors = true;
            }

            // Add valid loans to filtered list
            filteredLoans.push(loan);
        });

        return {valid: !hasErrors, errors, filteredLoans};
    }, [columns, bankLoanRequiredFields]);

    // Handle opening RepaymentScheduleDrawer for bank loan
    const handleOpenSchedule = useCallback((colId: string, loanData: Partial<BankLoanRow>) => {
        // Validate required fields
        if (!loanData.loanAmt || !loanData.terms || !loanData.firstRepay || !loanData.ratePct) {
            showSnackbar('Please fill in all required loan fields first', 'warning');
            return;
        }

        // Build schedule request
        // Convert firstRepay from dd/MM/yyyy to yyyy-MM-dd for RepaymentScheduleDrawer
        const scheduleRequest: LoanScheduleRequest = {
            loanAmount: loanData.loanAmt,
            tenorYears: loanData.terms / 12, // Convert months to years
            annualInterestRatePercent: loanData.ratePct, // Annual interest rate in percent
            firstRepaymentDate: convertToISOFormat(loanData.firstRepay), // Convert to yyyy-MM-dd
            interestFreeFrom: loanData.interestFreeFrom || null,
            interestFreeTo: loanData.interestFreeTo || null,
            principalFreeFrom: loanData.principalFreeFrom || null,
            principalFreeTo: loanData.principalFreeTo || null,
        };

        setCurrentScheduleRequest(scheduleRequest);
        setCurrentLoanColId(colId);
        setScheduleDrawerOpen(true);
    }, [showSnackbar]);

    // Handle RepaymentScheduleDrawer data loaded - update loan sums with recalculated data
    const handleScheduleDataLoaded = useCallback((data: RepaymentScheduleDrawerDataLoaded) => {
        if (!currentLoanColId) return;
        const existingLoan = loanDataByColumn[currentLoanColId];
        if (!existingLoan) return;

        // Schedule / recalc → base (split left) only; *Change stays keyboard-owned
        const incoming = data.sums as BankLoanSums;
        const mergedSums = isBankLoanScheduleUserRecalc(incoming)
            ? mergeBankLoanSumsFromScheduleRecalc((existingLoan as BankLoanRow).sums, incoming)
            : mergeBankLoanSumsPreserveChanges((existingLoan as BankLoanRow).sums, incoming);
        const updatedLoan: BankLoanRow = {
            ...(existingLoan as BankLoanRow),
            sums: mergedSums as BankLoanSums,
        };

        // Update bankLoans array: find the index of the current column and update
        const bankIdx = columns.filter(c => c.type === 'bank').findIndex(c => c.id === currentLoanColId);
        if (bankIdx < 0) return;

        const newBankLoans = [...bankLoans];
        newBankLoans[bankIdx] = updatedLoan;
        updateLoanData(newBankLoans);

        // Also re-apply calculator result to update rows display
        setTimeout(() => applyCalculatorResultToColumn(currentLoanColId), 0);
    }, [currentLoanColId, loanDataByColumn, columns, bankLoans, updateLoanData, applyCalculatorResultToColumn]);

    // 计算总金额 - 提取特定行的 total 列值
    const totals = useMemo(() => {
        // 查找特定行
        const bankInterestRow = rows.find(r => r.name === 'Bank mortgage loan (secured) Interest paid');
        const currentPortionRow = rows.find(r => r.name === 'Current portion');
        const nonCurrentPortionRow = rows.find(r => r.name === 'Non-current portion');

        // 计算每行的 total（所有列的和）
        const calculateRowTotal = (row: LoanTableRow | undefined) => {
            if (!row) return 0;
            return columns.reduce((acc, c) => acc + toNumber(row.values[c.id]), 0);
        };

        // 计算银行利息（本年 / 上年）
        const bankInterestTotal = calculateRowTotal(bankInterestRow);
        const bankInterestLastYear = columns
            .filter((c) => c.type === 'bank')
            .reduce((acc, c) => {
                const sums = loanDataByColumn[c.id]?.sums;
                if (!sums) return acc;
                const value = sums.interestLastYoaChange != null
                    ? sums.interestLastYoaChange
                    : (sums.interestLastYoa || 0);
                return acc + toNumber(value);
            }, 0);

        return {
            bankInterest: bankInterestTotal,
            bankInterestLastYear,
            currentPortion: calculateRowTotal(currentPortionRow),
            nonCurrentPortion: calculateRowTotal(nonCurrentPortionRow),
            final: calculateRowTotal(currentPortionRow) + calculateRowTotal(nonCurrentPortionRow)
        };
    }, [rows, columns, loanDataByColumn]);

    /** MULTI: Current / Non-current WpPerCheck first row always tracks last YOA Tab. */
    const lastYoaPortionTotals = useMemo(() => {
        const live = {
            currentPortion: totals.currentPortion || 0,
            nonCurrentPortion: totals.nonCurrentPortion || 0,
        };
        if (!isMultiPeriodTabs) return live;
        const periods = Array.isArray(payload?.periods) ? payload.periods : [];
        if (periods.length < 2) return live;
        const last = periods[periods.length - 1];
        const lastKey = String(last?.key || '').trim();
        const lastYoaKey = String(last?.yearOfAssessment || '').trim();
        const activeKey = String(payload?.periodScope?.sliceKey || '').trim();
        if (lastKey && activeKey && lastKey === activeKey) return live;

        const companyId = Number(payload?.companyId || 0);
        if (lastKey) {
            const draft = useAdjustDraftStore.getState().getUnifiedDetail(`${backendCode}__${lastKey}`);
            const fromDraft = extractBankLoans(draft);
            if (fromDraft.length > 0) return sumBankLoanPortions(fromDraft);
        }
        if (companyId > 0 && derivedAnnualId > 0 && lastYoaKey) {
            const cache = getWpYoaMapCache(companyId, derivedAnnualId, backendCode);
            if (cache?.byYearOfAssessment?.[lastYoaKey]) {
                const projected = projectYoaBucketToWorkingPaperData(cache, lastYoaKey);
                const fromCache = extractBankLoans(projected);
                if (fromCache.length > 0) return sumBankLoanPortions(fromCache);
            }
        }
        return live;
    }, [
        isMultiPeriodTabs,
        payload?.periods,
        payload?.periodScope?.sliceKey,
        payload?.companyId,
        derivedAnnualId,
        backendCode,
        totals.currentPortion,
        totals.nonCurrentPortion,
    ]);

    /** PL-FC-LI Client for one YOA column (= ProfitLoss Total Client-by-YOA), not engagement total. */
    const resolvePlFcLiYoaColumnClient = useCallback((yoaKey: string): number | null => {
        const key = String(yoaKey || '').trim();
        if (!key) return null;
        const byYoa = (hirePurchaseInterestLine as {amountsByYearOfAssessment?: Record<string, number | string>} | null)
            ?.amountsByYearOfAssessment;
        if (byYoa && typeof byYoa === 'object' && Object.keys(byYoa).length > 0) {
            const raw = byYoa[key];
            return (raw !== undefined && raw !== null && raw !== '') ? (Number(raw) || 0) : 0;
        }
        const amounts = resolvePlClientAmountsForYoa('PL-FC-LI', key);
        return amounts ? amounts.clientValue : null;
    }, [hirePurchaseInterestLine]);

    /** MULTI: Interest Per PL YOA / lastYoa columns ← ProfitLoss PL-FC-LI YOA column. */
    const interestPlPerFromTotalYoa = useMemo(() => {
        if (!isMultiPeriodTabs) return null;
        return resolvePlFcLiYoaColumnClient(stablePerCheckYoa.yoa);
    }, [isMultiPeriodTabs, stablePerCheckYoa.yoa, resolvePlFcLiYoaColumnClient, plLineRaw, payload?.periods]);

    const interestPlPerLastYoaFromTotal = useMemo(() => {
        if (!isMultiPeriodTabs) return null;
        return resolvePlFcLiYoaColumnClient(stablePerCheckYoa.lastYoa);
    }, [isMultiPeriodTabs, stablePerCheckYoa.lastYoa, resolvePlFcLiYoaColumnClient, plLineRaw, payload?.periods]);

    // 计算是否不平衡
    const isUnbalanced = useMemo(() => {
        const targetFinalAmount = (isControlled && payload?.line)
            ? (payload.line.finalAmount || 0)
            : (categoryLine?.finalAmount || 0);
        return calculateIsUnbalanced(targetFinalAmount, totals.final || 0);
    }, [isControlled, payload?.line, categoryLine?.finalAmount, totals.final]);

    const persist = async (opts?: WpPersistOptions): Promise<boolean> => {
        const {closeOnSuccess, silent} = resolveWpPersistOptions(opts);
        if (saving) return false;

        if (!isEditableMode) {
            if (!silent) showSnackbar('当前为只读模式，无法保存', 'warning');
            return false;
        }

        if (!validateIssueMarkBeforeSave()) {
            if (!silent) {
                showSnackbar('Please provide issue description when marked as issue', 'error');
            }
            return false;
        }

        // Validate bank loans data before saving
        // 从 loanDataByColumn 获取最新数据，因为它是基于 columns 和 bankLoans 计算的
        const allBankLoans = columns
            .filter(c => c.type === 'bank')
            .map(c => loanDataByColumn[c.id])
            .filter(loan => loan && Object.keys(loan).length > 0) as BankLoanRow[];

        // const {valid, errors, filteredLoans} = validateBankLoans(allBankLoans);

        // if (!valid) {
        //     setValidationErrors(errors);
        //     showSnackbar('Please fix validation errors before saving', 'error');
        //     return;
        // }

        // Clear validation errors if valid
        // setValidationErrors({});
        const filteredLoans = allBankLoans;

        setSaving(true);

        try {
            const validBankLoans = filteredLoans;

            // Add annualId and code to each bank loan
            // Use manual update values (xxxChange) over calculated values when assembling data
            const bankLoansWithAnnualId = validBankLoans.map(loan => {
                const sums = loan.sums || {};
                // Keep calculated xxx; manual override stays in *Change only (Detail left vs right).
                // Unset *Change / schedule overrides are omitted from wire (per-Tab edits only).
                // interestNext still mirrors effective current interest for WP table fallback.
                const finalSums = sanitizeBankLoanSumsForSave({
                    ...sums,
                    interestNext: sums.interestYoaChange != null ? sums.interestYoaChange : sums.interestNext,
                });
                const {adjustments: _adj, ...loanRest} = loan as BankLoanRow & {adjustments?: unknown};

                return {
                    ...loanRest,
                    // Persist b/f wire = Summary last-YOA Total (same as table / Detail)
                    currentTotalInput: resolveBfAmount(loan),
                    sums: finalSums,
                    annualId: derivedAnnualId,
                    code: 'BS-CL-BLS',
                };
            });

            // Build details payload for bank loans - 直接使用 bankLoans 数组作为 details
            const detailsPayload = sanitizeBankLoansDetailsForSave({
                bankLoans: bankLoansWithAnnualId,
            });

            // MULTI 多年保存（参照 RentalExpense 的数据传递方式 + CommonMaterialTestWp 的接口选择）：
            // 当前 Tab 保存时把贷款数据传递到其余年份 —— 贷款仍在期限内
            // （lastRepay ≥ 目标年 periodFrom）才传递；当年的贷款已到最后期限
            // （本年内还清）→ 本年计算完即忽略，不再传递。
            // MULTI 直连 batchSaveOrUpdate（POST /working-paper/batchSaveOrUpdate）；
            // SINGLE 保持 saveOrUpdate 现网单年路径。
            const issueMarkPayload = buildIssueMarkPayload(issueMarkState, payload?.line?.wpStatus, Boolean((payload as any)?.line?.outstanding));
            const saveYoa = String(
                payload?.periodScope?.yearOfAssessment
                || (payload as {yearOfAssessment?: string})?.yearOfAssessment
                || yoa
                || '',
            ).trim();
            const multiPeriodsForSave = Array.isArray(payload?.periods) ? payload.periods : [];
            const isMultiYoaSave = String(payload?.spanType || '').toUpperCase() === 'MULTI'
                && multiPeriodsForSave.length >= 2;
            // MULTI：往后年 Tab 后台跑 Summary Card 同口径 schedule 切窗，写入 bankLoans[].sums
            // （保留已有 *Change；无需打开 BankLoanDetail）
            const byYearPayload = isMultiYoaSave
                ? await buildBankLoanMultiYearBatchPayload({
                    periods: multiPeriodsForSave,
                    apiCode: backendCode,
                    companyId: Number(payload?.companyId) || 0,
                    annualId: derivedAnnualId,
                    activeYoa: saveYoa,
                    activeSliceKey: payload?.periodScope?.sliceKey || (payload as {sliceKey?: string})?.sliceKey || '',
                    activeDetails: detailsPayload,
                    getDetail: (key: string) => useAdjustDraftStore.getState().getUnifiedDetail(key),
                    issueMark: issueMarkPayload ?? null,
                })
                : null;

            if (isMultiYoaSave && saveYoa && byYearPayload) {
                // MULTI → batchSaveOrUpdate（全量 YOA Map，key=code）
                const runBatch = () => batchSaveOrUpdate({
                    companyId: Number(payload?.companyId),
                    annualId: derivedAnnualId,
                    workingPapers: {[backendCode]: byYearPayload},
                });
                if (requiresWpGlobalTx(backendCode)) {
                    await runWpSaveWithGlobalTx(runBatch);
                } else {
                    await runBatch();
                }
                notifyFsAdjustmentSilentRefresh(Number(payload?.companyId), derivedAnnualId, backendCode);
            } else {
                // SINGLE：勿带 yearOfAssessment，否则 saveOrUpdate 会把请求误判为 bs-multi
                //（无 YOA Map 缓存时抛 P8）。MULTI 直连 batch；此处仅兜底单年契约。
                await saveOrUpdate({
                    companyId: Number(payload?.companyId),
                    annualId: derivedAnnualId,
                    code: backendCode,
                    details: detailsPayload,
                    issueMark: issueMarkPayload,
                    ...(isMultiYoaSave && saveYoa ? {yearOfAssessment: saveYoa} : {}),
                    ...(byYearPayload ? {byYearOfAssessmentPayload: byYearPayload} : {}),
                });
            }

            if (!silent) {
                notifySaveResult('Bank loan adjustments saved successfully', 'success');
            }

            await invalidateWpSpecialSyncQueries(
                queryClient,
                Number(payload?.companyId),
                derivedAnnualId,
                backendCode,
                'BankLoan',
            );
            // list 无感：MULTI 已 notify；SINGLE 由 saveOrUpdate notify — 勿再 setDataSaved

            // 保存成功后把各年份 bucket 回写 YOA Map 缓存（同 RentalExpense）：
            // 后续自动保存仍以未打开年份的 bucket 作为批量回退，避免将其清空；
            // 同时重新打开各年 Tab 时缓存投影也能带出结转的贷款行。
            if (
                byYearPayload
                && Number(payload?.companyId) > 0
                && derivedAnnualId > 0
            ) {
                for (const [yoa, bucket] of Object.entries(byYearPayload)) {
                    const yoaKey = String(yoa || '').trim();
                    const bucketDetails = (bucket as {details?: unknown})?.details;
                    if (!yoaKey || !bucketDetails || typeof bucketDetails !== 'object') continue;
                    upsertWpYoaBucketDetails(
                        Number(payload?.companyId),
                        derivedAnnualId,
                        backendCode,
                        yoaKey,
                        bucketDetails as Record<string, unknown>,
                    );
                }
            }

            // 先關閉對話框，讓 FsAdjustment 能夠讀取到 dataSaved 狀態
            if (closeOnSuccess) {
                setTimeout(() => {
                    onClose();
                }, 100);
            }
            return true;
        } catch (error: any) {
            const errorMessage = error?.message || 'Failed to save bank loan adjustments';
            if (!silent) showSnackbar(`${errorMessage}`, 'error');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const saveDraft = async () => {
        await persist({closeOnSuccess: false, silent: false});
    };

    const [dialogContentEl, setDialogContentEl] = useState<HTMLElement | null>(null);
    const {
        status: autoSaveStatus,
        lastSavedAt,
    } = useWpIdleAutoSave({
        open,
        enabled: isEditableMode && !showFieldAdjustmentButton && initialized,
        persist,
        contentEl: dialogContentEl,
    });

    const updateLoanDataForColumn = useCallback((colId: string, data: Partial<BankLoanRow>) => {
        // 更新指定列的数据
        const columnToUpdate = columns.find(c => c.id === colId);
        if (!columnToUpdate) return;

        // 从 currentDataRef 获取最新的 bankLoans 数据
        const currentRefData = currentDataRef.current as BankLoanWorkingPaper;
        const existingBankLoans = currentRefData?.bankLoans || [];

        // 构建 column 到 bankLoan 的映射
        const updatedColumnData: Record<string, Partial<BankLoanRow>> = {};
        let bankIdx = 0;
        columns.forEach((col) => {
            if (col.type === 'bank') {
                if (col.id === colId) {
                    // 合并现有数据与新传入的数据（保留 id、headerId 等原有字段，同时更新新的 sums 字段）
                    const existingData = existingBankLoans[bankIdx] || {};
                    const mergedSums = data.sums ? {
                        ...(existingData.sums || {}),
                        ...data.sums,
                    } : existingData.sums;
                    updatedColumnData[col.id] = {
                        ...existingData,
                        ...data,
                        ...(mergedSums ? { sums: mergedSums } : {}),
                    };
                } else if (existingBankLoans[bankIdx]) {
                    // 使用现有数据
                    updatedColumnData[col.id] = existingBankLoans[bankIdx];
                }
                bankIdx++;
            }
        });

        // 根据类型分离数据
        const newBankLoans = columns
            .filter(c => c.type === 'bank')
            .map(c => updatedColumnData[c.id])
            .filter(loan => loan && Object.keys(loan).length > 0) as BankLoanRow[];

        // 更新数据到store（包含完整的 sums 对象）
        updateLoanData(newBankLoans);

        // 同时更新 rows 状态以刷新表格显示
        const bankData = data as Partial<BankLoanRow>;
        setRows(prevRows => {
            const updated = prevRows.map((r) => {
                const next = {...r, values: {...r.values}};

                if (r.name === 'Bank mortgage loan (secured) Interest paid') {
                    const interestValue = bankData?.sums?.interestYoaChange != null 
                        ? bankData.sums.interestYoaChange 
                        : (bankData?.sums?.interestNext || 0);
                    next.values[colId] = formatNumber(interestValue);
                } else if (r.name === 'Current portion') {
                    const principalNextValue = bankData?.sums?.currentYoaChange != null 
                        ? bankData.sums.currentYoaChange 
                        : (bankData?.sums?.currentYoa || 0);
                    next.values[colId] = formatNumber(principalNextValue);
                } else if (r.name === 'Non-current portion') {
                    const principalAfterNextToValue = bankData?.sums?.nonCurrentYoaChange != null 
                        ? bankData.sums.nonCurrentYoaChange 
                        : (bankData?.sums?.nonCurrentYoa || 0);
                    next.values[colId] = formatNumber(principalAfterNextToValue);
                } else if (r.id === 7) {
                    const principalNextValue = bankData?.sums?.currentYoaChange != null 
                        ? bankData.sums.currentYoaChange 
                        : (bankData?.sums?.currentYoa || 0);
                    const principalAfterNextToValue = bankData?.sums?.nonCurrentYoaChange != null 
                        ? bankData.sums.nonCurrentYoaChange 
                        : (bankData?.sums?.nonCurrentYoa || 0);
                    const sum = principalNextValue + principalAfterNextToValue;
                    next.values[colId] = formatNumber(sum);
                } else if (r.name === 'b/f (Last year audit report)') {
                    next.values[colId] = formatNumber(resolveBfAmount(bankData));
                } else if (r.name === 'Newly obtained bank loan') {
                    next.values[colId] = formatNumber(
                        calcBankLoanNewlyObtained(bankData, activePeriodFrom),
                    );
                } else if (r.name === 'Repayment during the year') {
                    const principalCurrValue = bankData?.sums?.currentLastYoaChange != null 
                        ? bankData.sums.currentLastYoaChange 
                        : (bankData?.sums?.currentLastYoa || 0);
                    next.values[colId] = formatNumber(toNegativeBankLoanRepayment(principalCurrValue));
                }
                return next;
            });

            // c/f = Detail Summary YOA Total（与 getLoanAmountBase / 结转同源）
            const cfIndex = updated.findIndex((r) => r.name === 'c/f');
            if (cfIndex >= 0) {
                const cf = {...updated[cfIndex], values: {...updated[cfIndex].values}} as LoanTableRow;
                columns.forEach((col) => {
                    if (col.type !== 'bank') return;
                    cf.values[col.id] = formatNumber(
                        bankLoanPriorYearEndBalance(updatedColumnData[col.id] as BankLoanRow | undefined),
                    );
                });
                const nextRows = [...updated];
                nextRows[cfIndex] = cf;
                return nextRows;
            }
            return updated;
        });

        // 如果是受控模式，同时更新父组件
        if (isControlled && payload?.setParDetail) {
            const updatedData: BankLoanWorkingPaper = {
                bankLoans: newBankLoans,
            };
            payload.setParDetail(updatedData);
        }
    }, [columns, updateLoanData, isControlled, payload, activePeriodFrom, resolveBfAmount]);

    // Amount metric headers (formerly first-column row labels)
    const amountHeaders = useMemo(() => ([
        {key: 'currentPortion', name: 'Current portion', boldLeft: false},
        {key: 'nonCurrentPortion', name: 'Non-current portion', boldLeft: false},
        // Bold separator after Non-current portion
        {key: 'interest', name: 'Interest', boldLeft: true},
        // Bold separator after Interest (before b/f)
        {key: 'bf', name: 'b/f', boldLeft: true},
        {key: 'newlyObtained', name: 'Newly obtained bank loan', boldLeft: false},
        {key: 'repayment', name: 'Repayment during the year', boldLeft: false},
        {key: 'cf', name: 'c/f', boldLeft: false},
    ] as const), []);

    /** fromPaper code for field-level WP→WP adjustment icons */
    const fieldAdjustFromCodeByKey: Record<string, string> = {
        currentPortion: 'BS-CL-BLS',
        nonCurrentPortion: 'BS-NCL-BLS',
        interest: 'PL-FC-LI',
    };

    /** Per-loan adjustments list key for current / non-current / interest */
    const fieldAdjListKeyByAmountKey: Record<string, 'currAdj' | 'nonCurrAdj' | 'inAdj'> = {
        currentPortion: 'currAdj',
        nonCurrentPortion: 'nonCurrAdj',
        interest: 'inAdj',
    };

    const amountRowNameByKey: Record<string, string> = {
        currentPortion: 'Current portion',
        nonCurrentPortion: 'Non-current portion',
        bf: 'b/f (Last year audit report)',
        newlyObtained: 'Newly obtained bank loan',
        repayment: 'Repayment during the year',
        cf: 'c/f',
    };

    /** Prefer xxxChange when set, else base sum field (matches BankLoanDetail / repayment schedule). */
    const resolveBankSumField = useCallback((
        sums: Partial<BankLoanSums> | null | undefined,
        baseKey: keyof BankLoanSums,
        changeKey: keyof BankLoanSums,
    ) => {
        if (!sums) return 0;
        const change = sums[changeKey];
        if (change != null) return toNumber(change);
        return toNumber(sums[baseKey]);
    }, []);

    /** Align with BankLoanDetail summary Interest (current year): prefer interestYoaChange, else interestYoa. */
    const resolveBankInterestCurr = useCallback((sums?: Partial<BankLoanSums> | null) => {
        return resolveBankSumField(sums, 'interestYoa', 'interestYoaChange');
    }, [resolveBankSumField]);

    /** Base amount without loan-field adjustments (for LoanFieldAdjustButton perClient). */
    const getLoanAmountBase = useCallback((colId: string, amountKey: string) => {
        const sums = loanDataByColumn[colId]?.sums as Partial<BankLoanSums> | undefined;
        if (amountKey === 'currentPortion') {
            return resolveBankSumField(sums, 'currentYoa', 'currentYoaChange');
        }
        if (amountKey === 'nonCurrentPortion') {
            return resolveBankSumField(sums, 'nonCurrentYoa', 'nonCurrentYoaChange');
        }
        if (amountKey === 'interest') {
            return resolveBankInterestCurr(sums);
        }
        // Detail SummaryCard last-YOA Total (Change ?? balanceLastYoa ?? prior/currentTotalInput)
        if (amountKey === 'bf') {
            return resolveBfAmount(loanDataByColumn[colId] as BankLoanRow | undefined);
        }
        // This Tab only: Newly obtained vs active Tab periodFrom
        if (amountKey === 'newlyObtained') {
            return calcBankLoanNewlyObtained(
                loanDataByColumn[colId] as BankLoanRow | undefined,
                activePeriodFrom,
            );
        }
        // YOA Principal total from persisted / Drawer sums — outflow (negative) on WP table
        if (amountKey === 'repayment') {
            return toNegativeBankLoanRepayment(
                resolveBankSumField(sums, 'currentLastYoa', 'currentLastYoaChange'),
            );
        }
        // c/f = Detail Summary YOA Total (<1yr + >1yr), same as MULTI roll / bankLoanPriorYearEndBalance.
        // WHY: b/f+newly+repay drifts when lastYoa empty or currentLastYoaChange stale.
        if (amountKey === 'cf') {
            return bankLoanPriorYearEndBalance(loanDataByColumn[colId] as BankLoanRow | undefined);
        }
        const rowName = amountRowNameByKey[amountKey];
        const row = rows.find((r) => r.name === rowName);
        return toNumber(row?.values[colId]);
    }, [rows, loanDataByColumn, resolveBankSumField, resolveBankInterestCurr, activePeriodFrom, resolveBfAmount]);

    /** Display = base + Σ adjustments on that loan (currAdj / nonCurrAdj / inAdj). */
    const getLoanAmountValue = useCallback((colId: string, amountKey: string) => {
        const base = getLoanAmountBase(colId, amountKey);
        const adjKey = fieldAdjListKeyByAmountKey[amountKey];
        if (!adjKey) return base;
        const loan = loanDataByColumn[colId] as BankLoanRow | undefined;
        return base + sumLoanFieldAdjList(loan?.adjustments?.[adjKey]);
    }, [getLoanAmountBase, loanDataByColumn]);

    const getAmountColumnTotal = useCallback((amountKey: string) => {
        return columns
            .filter((c) => c.type === 'bank')
            .reduce((acc, c) => acc + toNumber(getLoanAmountValue(c.id, amountKey)), 0);
    }, [columns, getLoanAmountValue]);

    const boldSeparatorSx = {
        // inset shadow survives border-collapse (Total row borderTop would swallow borderLeft)
        boxShadow: (theme: any) => `inset 2px 0 0 0 ${theme.palette.text.primary}`,
    };

    /**
     * Unified content width = max(toolbar single-row intrinsic, table header intrinsic).
     * Parent uses fit-content + column flex; both children use minWidth:max-content + width:100%
     * so the wider of the two drives dialog width, and the narrower stretches to match.
     */
    const unifiedContentSx = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: 'fit-content',
        maxWidth: '95vw',
        height: '100%',
    } as const;

    const intrinsicStretchSx = {
        width: '100%',
        minWidth: 'max-content',
        flexShrink: 0,
    } as const;

    const bankLoanContent = (
        <Box sx={intrinsicStretchSx}>
            <Box sx={{mb: 1}}>
                <WpNotesAnchor/>
            </Box>
            <TableContainer sx={{width: '100%', minWidth: 'max-content'}}>
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        width: '100%',
                        minWidth: 'max-content',
                        tableLayout: 'auto',
                        '& td, & th': {whiteSpace: 'nowrap'},
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">
                                <Typography variant="subtitle2" noWrap>Bank</Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography variant="subtitle2" noWrap>Loan ac no</Typography>
                            </TableCell>
                            {amountHeaders.map((h) => (
                                <TableCell
                                    key={h.key}
                                    align="center"
                                    sx={h.boldLeft ? boldSeparatorSx : undefined}
                                >
                                    <Typography variant="subtitle2" noWrap>{h.name}</Typography>
                                </TableCell>
                            ))}
                            <TableCell align="center">
                                {isEditableMode && (
                                    <Tooltip title="Add Bank Loan" arrow>
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={openAddBankLoanDialog}
                                            aria-label="Add Bank Loan"
                                        >
                                            <AddIcon fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{'& .MuiTableCell-root': {paddingTop: '2px', paddingBottom: '2px'}}}>
                        {columns.map((c) => {
                            const loanData = loanDataByColumn[c.id] || {};
                            return (
                                <TableRow key={c.id} hover>
                                    <TableCell align="center" sx={{verticalAlign: 'middle'}}>
                                        <Typography variant="body2" noWrap>{loanData.bank || '-'}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{verticalAlign: 'middle'}}>
                                        <Typography variant="body2" noWrap>{loanData.loanNo || '-'}</Typography>
                                    </TableCell>
                                    {amountHeaders.map((h) => (
                                        <TableCell
                                            key={h.key}
                                            align="center"
                                            sx={{
                                                verticalAlign: 'middle',
                                                ...(h.boldLeft ? boldSeparatorSx : {}),
                                            }}
                                        >
                                            <Box sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.25,
                                                justifyContent: 'center',
                                            }}>
                                                {(() => {
                                                    const adjKey = fieldAdjListKeyByAmountKey[h.key];
                                                    const fieldAdj = adjKey
                                                        ? getLoanRowFieldAdj((loanData as BankLoanRow).adjustments, adjKey)
                                                        : undefined;
                                                    const itemLabel = [
                                                        String(loanData.bank || loanData.loanNo || '').trim(),
                                                        h.name,
                                                    ].filter(Boolean).join(' — ') || h.name;
                                                    return (
                                                        <LoanAdjAmountBadge
                                                            value={getLoanAmountValue(c.id, h.key)}
                                                            currencySymbol={payload?.currencySymbol}
                                                            adjustments={fieldAdj}
                                                            itemLabel={itemLabel}
                                                            onOpenDetails={adjDrawer.handleOpen}
                                                        />
                                                    );
                                                })()}
                                                {!isControlled && fieldAdjustFromCodeByKey[h.key] && (
                                                    <LoanFieldAdjustButton
                                                        fromCode={fieldAdjustFromCodeByKey[h.key]}
                                                        itemName={String(loanData.bank || loanData.loanNo || '').trim() || h.name}
                                                        fieldLabel={h.name}
                                                        perClient={toNumber(fieldAdjustPerClientByKey[h.key])}
                                                        existingAdjustments={getLoanRowFieldAdj(
                                                            (loanData as BankLoanRow).adjustments,
                                                            fieldAdjListKeyByAmountKey[h.key],
                                                        )}
                                                        statement={h.key === 'interest' ? 'PL' : 'BS'}
                                                        onActivate={handleFieldAdjustActivate}
                                                    />
                                                )}
                                            </Box>
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" sx={{verticalAlign: 'middle'}}>
                                        <Box sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            justifyContent: 'center',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            <AttachmentIconButton
                                                targetType="bank_loan"
                                                // Must use persisted bank_loan row id — column id is a random string (Number → NaN)
                                                targetId={Number((loanData as BankLoanRow)?.id) || 0}
                                                companyId={payload?.companyId ? Number(payload.companyId) : undefined}
                                                annualId={derivedAnnualId}
                                                onClick={() => handleOpenAttachment(c.id)}
                                            />
                                            {!isEditableMode && loanData && Object.keys(loanData).length > 0 && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleOpenSchedule(c.id, loanData)}
                                                    title="View Repayment Schedule"
                                                    sx={{color: 'primary.main'}}
                                                >
                                                    <CalendarMonthIcon fontSize="small"/>
                                                </IconButton>
                                            )}
                                            {c.type === 'bank' && (
                                                <BankLoanDetails
                                                    yoa={yoa || ""}
                                                    lastYoa={(isMultiPeriodTabs ? displayLastYoa : lastYoa) || ""}
                                                    currencySymbol={currency || payload?.currencySymbol || 'HKD'}
                                                    triggerVariant="icon"
                                                    annualId={payload?.annualId}
                                                    initialData={(loanData as Partial<BankLoanRow>) || null}
                                                    onDataChange={(data) => updateLoanDataForColumn(c.id, data)}
                                                    onConfirm={() => applyCalculatorResultToColumn(c.id)}
                                                    readOnly={!isEditableMode}
                                                    lockCoreFields={isMultiPeriodTabs && !isFirstYoaTab}
                                                    lastYoaColumnDisplay={
                                                        isMultiPeriodTabs && !isFirstYoaTab
                                                            ? resolveLastYoaColumnDisplay(loanData as BankLoanRow)
                                                            : null
                                                    }
                                                    hideLastYoa={firstAudit === 1 && (!isMultiPeriodTabs || isFirstYoaTab)}
                                                    inCorpDate={inCorpDate}
                                                    periodTo={periodTo}
                                                    periodWindow={isMultiPeriodTabs ? tabPeriodWindow : null}
                                                    onOcrProcessingStart={handleOcrProcessingStart}
                                                    onOcrProcessingComplete={handleOcrProcessingComplete}
                                                    onOcrProcessingError={handleOcrProcessingError}
                                                    ocrProcessingOpen={ocrProcessingOpen}
                                                />
                                            )}
                                            {canShowBankLoanDelete && (
                                                <PopoverButton
                                                    message={`Remove ${String(loanData.bank || loanData.loanNo || 'this bank loan').trim()} from all years?`}
                                                    confirmLabel="Yes"
                                                    cancelLabel="No"
                                                    loading={deletingLoanColId === c.id}
                                                    popoverZIndex={1400}
                                                    onConfirm={() => removeColumn(c.id)}
                                                    trigger={
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            disabled={deletingLoanColId === c.id}
                                                            aria-label="Delete bank loan"
                                                        >
                                                            <DeleteOutlineIcon fontSize="small"/>
                                                        </IconButton>
                                                    }
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {/* Total row at bottom */}
                        <TableRow>
                            <TableCell
                                align="center"
                                sx={{
                                    verticalAlign: 'middle',
                                    fontWeight: 'bold',
                                    borderTop: '2px solid',
                                    borderTopColor: 'divider',
                                }}
                            >
                                <Typography variant="subtitle2" sx={{fontWeight: 'bold'}} noWrap>Total</Typography>
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    verticalAlign: 'middle',
                                    fontWeight: 'bold',
                                    borderTop: '2px solid',
                                    borderTopColor: 'divider',
                                }}
                            />
                            {amountHeaders.map((h) => (
                                <TableCell
                                    key={h.key}
                                    align="center"
                                    sx={{
                                        verticalAlign: 'middle',
                                        fontWeight: 'bold',
                                        borderTop: '2px solid',
                                        borderTopColor: 'divider',
                                        ...(h.boldLeft ? boldSeparatorSx : {}),
                                    }}
                                >
                                    <MoneyField
                                        value={getAmountColumnTotal(h.key)}
                                        currencySymbol={payload?.currencySymbol}
                                        showCents={true}
                                        align="center"
                                        compact={true}
                                    />
                                </TableCell>
                            ))}
                            <TableCell
                                align="center"
                                sx={{
                                    verticalAlign: 'middle',
                                    fontWeight: 'bold',
                                    borderTop: '2px solid',
                                    borderTopColor: 'divider',
                                }}
                            />
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
            <LoanFieldAdjustPanel
                target={fieldAdjustTarget}
                onClose={handleFieldAdjustClose}
                companyId={payload?.companyId ? Number(payload.companyId) : undefined}
                annualId={derivedAnnualId}
                currency={currency || payload?.currencySymbol}
                onParentClose={onClose}
                onActionStateChange={handleFieldAdjustActionStateChange}
                buildLoanSaveDetails={buildBankLoanSaveDetails}
            />
            <EpWorkingContent
                    companyId={Number(payload?.companyId)}
                    annualId={Number(payload?.annualId) || derivedAnnualId}
                    fsId={Number(payload?.line?.fsId) || undefined}
                    initialNotes={(payload?.line as any)?.notes}
                    smeFrsData={isMultiPeriodTabs ? epWorkingMeta.smeFrs : currentData?.['SME-FRS']}
                    wokeDoneData={isMultiPeriodTabs ? epWorkingMeta.wokeDone : currentData?.['WOKE_DONE']} />

            {/* Add flow: open details first; only commit a new row after Confirm */}
            {isEditableMode && (
                <BankLoanDetails
                    key={addDialogKey}
                    yoa={yoa || ""}
                    lastYoa={(isMultiPeriodTabs ? displayLastYoa : lastYoa) || ""}
                    currencySymbol={currency || payload?.currencySymbol || 'HKD'}
                    hideTrigger
                    open={addDialogOpen}
                    onOpenChange={(next) => {
                        if (!next) setAddDialogOpen(false);
                    }}
                    deferDataChangeUntilConfirm
                    annualId={payload?.annualId}
                    initialData={null}
                    onConfirm={handleAddBankLoanConfirm}
                    onCancel={handleAddBankLoanCancel}
                    hideLastYoa={firstAudit === 1}
                    inCorpDate={inCorpDate}
                    periodTo={periodTo}
                    periodWindow={isMultiPeriodTabs ? tabPeriodWindow : null}
                    onOcrProcessingStart={handleOcrProcessingStart}
                    onOcrProcessingComplete={handleOcrProcessingComplete}
                    onOcrProcessingError={handleOcrProcessingError}
                    ocrProcessingOpen={ocrProcessingOpen}
                />
            )}
        </Box>
    );

    if (embed) {
        return (
            <>
                {bankLoanContent}
                <OcrUploadProcessing
                    open={ocrProcessingOpen}
                    processingComplete={ocrProcessingComplete}
                    onFinished={handleOcrProcessingFinished}
                />
            </>
        );
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={(event, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') onClose(); }}
                maxWidth={false}
                sx={{
                    '& .MuiDialog-paper': {
                        width: 'fit-content',
                        maxWidth: '95vw',
                    },
                    '& .MuiDialogContent-root': {
                        overflowX: 'auto',
                    },
                }}
            >
                <DialogTitle>{companyName || 'Bank Loan Working Paper'}</DialogTitle>
                <DialogContent ref={dialogContentElRef(setDialogContentEl)} dividers>
                    {(loading || !initialized) ? (
                        <LoanWpSkeleton variant="bank"/>
                    ) : (
                        <Box sx={unifiedContentSx}>
                            {/* Sticky header: Mark as issue + checks in one row */}
                            <Box sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 100,
                                backgroundColor: 'background.paper',
                                ...intrinsicStretchSx,
                            }}>
                                <WpHeader
                                    itemName={scheduleName}
                                    workingName={'Bank Loan Working Paper'}
                                    periodFrom={headerPeriodFrom}
                                    periodTo={headerPeriodTo}
                                    preparedBy={payload?.preparedBy}
                                    reviewedBy={payload?.reviewedBy}
                                    dateText={today}
                                    hidePeriodTabs={isMultiPeriodTabs}
                                />

                                {payload?.workflow === 'account' && (
                                    <Box sx={{display: 'flex', justifyContent: 'flex-start', mb: 1}}>
                                        <WpGlInvoiceDetailButton onClick={() => glDetail.setOpen(true)} disabled={glDetail.rows.length === 0}/>
                                    </Box>
                                )}

                                <Divider sx={{my: 2}}/>

                                <Box
                                    sx={{
                                        ...intrinsicStretchSx,
                                        '& > .MuiBox-root': {flexWrap: 'nowrap'},
                                    }}
                                >
                                    <WpCheckToolbar
                                        code={backendCode}
                                        companyId={Number(payload?.companyId)}
                                        annualId={derivedAnnualId}
                                        initialWpStatus={currentData?.wpStatus ?? payload?.line?.wpStatus}
                                        initialWpIssueRemark={currentData?.wpIssueRemark ?? payload?.line?.wpIssueRemark}
                                        initialOutstanding={Boolean(currentData?.outstanding ?? payload?.line?.outstanding)}
                                        initialOutstandingRemark={currentData?.outstandingRemark ?? payload?.line?.outstandingRemark}
                                        onIssueMarkChange={setIssueMarkState}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'flex-end',
                                                gap: 3,
                                                mb: 1,
                                                width: '100%',
                                                minWidth: 'max-content',
                                                flexWrap: 'nowrap',
                                            }}
                                        >
                                            {/* 1. Current portion, ignoreLastYear=true */}
                                            <Box sx={{minWidth: 280, maxWidth: 320, flexShrink: 0}}>
                                                <WpPerCheck
                                                    payload={{
                                                        perName: 'Current portion',
                                                        currentValue: lastYoaPortionTotals.currentPortion || 0,
                                                        lastYearValue: 0,
                                                        yoa: '',
                                                        lastYoa: '',
                                                    }}
                                                    code="BS-CL-BLS"
                                                    currentPer={(ok) => setCurrentPortionPerOk(ok)}
                                                    options={{ignoreLastYear: true}}
                                                />
                                            </Box>

                                            {/* 2. Non-current portion, ignoreLastYear=true */}
                                            <Box sx={{minWidth: 280, maxWidth: 320, flexShrink: 0}}>
                                                <WpPerCheck
                                                    payload={{
                                                        perName: 'Non-current portion',
                                                        currentValue: lastYoaPortionTotals.nonCurrentPortion || 0,
                                                        lastYearValue: 0,
                                                        yoa: '',
                                                        lastYoa: '',
                                                    }}
                                                    code="BS-NCL-BLS"
                                                    currentPer={(ok) => setNonCurrentPortionPerOk(ok)}
                                                    options={{ignoreLastYear: true}}
                                                />
                                            </Box>

                                            {/* 3. interest paid — Bank + Vehicle vs PL-FC-LI */}
                                            <Box sx={{minWidth: 280, maxWidth: 360, flexShrink: 0}}>
                                                <WpInterestPerCheck
                                                    payload={{
                                                        yoa: stablePerCheckYoa.yoa,
                                                        lastYoa: stablePerCheckYoa.lastYoa,
                                                    }}
                                                    sourceOverride={{
                                                        source: 'bank',
                                                        currentValue: getAmountColumnTotal('interest') || 0,
                                                        lastYearValue: totals.bankInterestLastYear || 0,
                                                    }}
                                                    annualId={derivedAnnualId}
                                                    companyId={Number(payload?.companyId || 0)}
                                                    currentPer={(ok) => setInterestPerOk(ok)}
                                                    auditedValueOverride={interestPlPerFromTotalYoa ?? undefined}
                                                    lastYearAuditedOverride={interestPlPerLastYoaFromTotal ?? undefined}
                                                />
                                            </Box>
                                        </Box>
                                    </WpCheckToolbar>
                                </Box>
                                <Divider sx={{mb: 1}}/>
                                <WpSeamlessPeriodTabsSlot enabled={isMultiPeriodTabs}/>
                            </Box>
                            {bankLoanContent}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <AutoSaveStatus status={autoSaveStatus} lastSavedAt={lastSavedAt}/>
                    {/* Field adjust: adj ≠ 0 → Adjustment; otherwise Save */}
                    {isEditableMode && showFieldAdjustmentButton && (
                        <Button
                            variant="contained"
                            onClick={() => fieldAdjustAction?.submitAdjustment()}
                            disabled={saving || loading || !!fieldAdjustAction?.isProcessing}
                            sx={{mr: 1}}
                            endIcon={fieldAdjustAction?.isProcessing ? <CircularProgress size={16}/> : null}
                        >
                            {fieldAdjustAction?.isProcessing ? 'Processing...' : 'Adjustment'}
                        </Button>
                    )}
                    {isEditableMode && !showFieldAdjustmentButton && (
                        <Button
                            variant="contained"
                            onClick={saveDraft}
                            disabled={saving || loading}
                            sx={{mr: 1}}
                            startIcon={saving ? <CircularProgress size={16}/> : null}
                        >{saving ? 'Saving...' : 'Save'}
                        </Button>
                    )}
                    <Button
                        onClick={onClose}
                        disabled={saving}
                    >{isEditableMode ? 'Cancel' : 'Close'}</Button>
                </DialogActions>
                <SnackbarField {...snackbarFieldProps} />
            </Dialog>

            {payload?.workflow === 'account' && (
                <WpGlInvoiceDetailDialog open={glDetail.open} onClose={() => glDetail.setOpen(false)}
                    code={glCode} total={glDetail.total} rows={glDetail.rows}/>
            )}

            {/* RepaymentScheduleDrawer */}
            <RepaymentScheduleDrawer
                open={scheduleDrawerOpen}
                onClose={() => setScheduleDrawerOpen(false)}
                loanType="bank"
                annualId={derivedAnnualId}
                scheduleRequest={currentScheduleRequest}
                periodWindow={isMultiPeriodTabs ? tabPeriodWindow : null}
                earlyRepay={
                    (currentLoanColId
                        ? (loanDataByColumn[currentLoanColId] as BankLoanRow | undefined)?.earlyRepay
                        : null) || null
                }
                onDataLoaded={handleScheduleDataLoaded}
            />
            <RichTextEditorDrawer
                open={attachmentDialogOpen}
                onClose={() => setAttachmentDialogOpen(false)}
                targetType="bank_loan"          // 与后端约定，例如 'bank_loan'
                itemName={currentAttachmentName}
                targetId={currentAttachmentLoanId ?? 0}
                userId={userId}
                 companyId={payload?.companyId ? Number(payload.companyId) : undefined}
                annualId={derivedAnnualId}
            />
            <AdjustmentDetailsDrawer
                open={adjDrawer.open}
                onClose={adjDrawer.handleClose}
                itemName={adjDrawer.itemName}
                currency={currency || payload?.currencySymbol || 'HKD'}
                headerData={adjDrawer.headerData}
                headerDataLoading={adjDrawer.loading}
            />
            <OcrUploadProcessing
                open={ocrProcessingOpen}
                processingComplete={ocrProcessingComplete}
                onFinished={handleOcrProcessingFinished}
            />
        </>
    );
}
